/* Style Mobo — Custom Variant Picker */

class SMVariantPicker {
  constructor(el) {
    this.el      = el;
    this.secId   = el.dataset.section;
    this.formId  = el.dataset.formId;
    this.variants = JSON.parse(
      el.querySelector(`#sm-vdata-${this.secId}`).textContent
    );
    this.isCombo = this.el.dataset.combo === 'true';
    this.el.__smVP = this;
    this.selections = this._readSelections();
    this._bindEvents();
    this._initCombobox();
    if (this.isCombo) {
      this._filterComboModels('1');
      this._filterComboModels('2');
    } else {
      this._filterModels();
    }
    this.el.querySelectorAll('.sm-vp-group--dropdown').forEach(g => this._syncCombobox(g));
    this._updateCustomText();
    this._sync(false);
    this._updateStepStatus();
  }

  /* ── Numbered step rail: swap number → checkmark once a step has a value ── */
  _updateStepStatus() {
    const checkSvg = '<svg viewBox="0 0 16 16" fill="none" width="11" height="11"><path d="M3 8.5l3 3 7-7" stroke="#FFF" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    this.el.querySelectorAll('.sm-vp-step').forEach(step => {
      const group = step.querySelector('.sm-vp-group');
      let complete = false;
      if (group?.dataset.comboField === 'brand' || group?.dataset.comboField === 'model') {
        complete = !!group.querySelector('select')?.value;
      } else if (group?.dataset.comboField === 'color') {
        complete = !!group.querySelector('input[type="radio"]:checked');
      } else if (group?.dataset.optionPos !== undefined && group.dataset.optionPos !== '') {
        complete = !!this.selections[parseInt(group.dataset.optionPos)];
      } else if (group?.hasAttribute('data-custom-text-group')) {
        complete = !!group.querySelector('input[type="radio"]:checked');
      }
      step.classList.toggle('is-complete', complete);
      const numEl = step.querySelector('[data-step-num]');
      if (numEl) numEl.innerHTML = complete ? checkSvg : step.dataset.step;
    });

    if (this.isCombo) this._updateComboAccordions();
  }

  /* ── Combo picker: per-cover accordion summary text + completion state,
       plus auto-advance to Cover 2 once Cover 1 is fully answered. ── */
  _updateComboAccordions() {
    ['1', '2'].forEach(cover => {
      const acc = this.el.querySelector(`[data-cover-accordion="${cover}"]`);
      if (!acc) return;

      const brand  = acc.querySelector(`select[data-combo-field="brand"][data-cover="${cover}"]`)?.value;
      const model  = acc.querySelector(`select[data-combo-field="model"][data-cover="${cover}"]`)?.value;
      const color  = acc.querySelector(`.sm-vp-group[data-combo-field="color"][data-cover="${cover}"] input[type="radio"]:checked`)?.value;
      const pos    = cover === '1' ? 0 : 1;
      const hasMaterialStep = !!acc.querySelector('.sm-vp-group[data-option-pos]');
      const material = hasMaterialStep ? this.selections[pos] : true;
      const ctChecked = acc.querySelector('[data-custom-text-group] input[type="radio"]:checked');

      const complete = !!(brand && model && color && material && ctChecked);
      acc.classList.toggle('is-complete', complete);

      const parts = [brand, model, color, hasMaterialStep ? material : null].filter(Boolean);
      const summaryEl = acc.querySelector(`[data-cover-summary="${cover}"]`);
      if (summaryEl) {
        summaryEl.textContent = parts.length ? parts.join(' · ') : 'Choose brand, model & more';
      }

      if (complete && cover === '1' && !this._cover1AutoAdvanced) {
        this._cover1AutoAdvanced = true;
        const cover2 = this.el.querySelector('[data-cover-accordion="2"]');
        if (acc.open && cover2 && !cover2.open) {
          acc.open = false;
          cover2.open = true;
        }
      }
    });
  }

  /* ── Read current selections from all groups ── */
  _readSelections() {
    const sel = [];
    this.el.querySelectorAll('.sm-vp-group[data-option-pos]').forEach(group => {
      const pos    = parseInt(group.dataset.optionPos);
      const select = group.querySelector('select');
      const radio  = group.querySelector('input[type="radio"]:checked');
      const val    = select ? select.value : (radio ? radio.value : null);
      sel[pos]     = val || null; // treat empty string placeholder as null
    });
    return sel;
  }

  /* ── Custom dropdown (Brand / Model) — a real <select> stays in the DOM
       (visually hidden) as the source of truth for form/selection logic;
       this just drives its value from a fully-styled trigger + listbox so
       the open-state UI matches the site instead of the OS-native list,
       and so filtering (below) can hide options reliably. ── */
  _initCombobox() {
    this.el.querySelectorAll('[data-combobox]').forEach(box => {
      const trigger = box.querySelector('[data-combobox-trigger]');
      const list    = box.querySelector('[data-combobox-list]');
      const search  = box.querySelector('[data-combobox-search]');
      const native  = box.querySelector('select');
      if (!trigger || !list || !native) return;

      const closeList = () => {
        list.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        if (search) {
          search.value = '';
          this._filterComboboxBySearch(list, '');
        }
      };
      const openList = () => {
        this.el.querySelectorAll('[data-combobox-list]').forEach(l => { l.hidden = true; });
        this.el.querySelectorAll('[data-combobox-trigger]').forEach(t => t.setAttribute('aria-expanded', 'false'));
        list.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        if (search) search.focus();
      };

      trigger.addEventListener('click', () => {
        if (list.hidden) openList(); else closeList();
      });

      list.querySelectorAll('[data-combobox-option]').forEach(li => {
        li.addEventListener('click', () => {
          if (li.hasAttribute('aria-disabled')) return;
          native.value = li.dataset.value;
          native.dispatchEvent(new Event('change', { bubbles: true }));
          closeList();
        });
      });

      if (search) {
        search.addEventListener('input', () => this._filterComboboxBySearch(list, search.value));
        search.addEventListener('click', e => e.stopPropagation());
        search.addEventListener('keydown', e => {
          if (e.key === 'Escape') { closeList(); trigger.focus(); }
        });
      }

      document.addEventListener('click', e => {
        if (!box.contains(e.target)) closeList();
      });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape') closeList();
      });
    });
  }

  /* ── Type-to-filter within one combobox's listbox. Composes with the
       Brand→Model cascade below rather than fighting it: brand-validity
       lives in data-brand-hidden, search-match in data-search-hidden, and
       an option is only shown when neither flag is set. ── */
  _filterComboboxBySearch(list, query) {
    const q = query.trim().toLowerCase();
    let visibleCount = 0;
    list.querySelectorAll('[data-combobox-option]').forEach(li => {
      const matches = !q || li.textContent.toLowerCase().includes(q);
      li.dataset.searchHidden = matches ? 'false' : 'true';
      this._applyOptionVisibility(li);
      if (!li.hidden) visibleCount++;
    });
    const empty = list.querySelector('[data-combobox-empty]');
    if (empty) empty.hidden = visibleCount > 0;
  }

  /* ── Final visibility = not brand-hidden AND not search-hidden. ── */
  _applyOptionVisibility(li) {
    const brandHidden  = li.dataset.brandHidden === 'true';
    const searchHidden = li.dataset.searchHidden === 'true';
    li.hidden = brandHidden || searchHidden;
  }

  /* ── Sync one combobox's visible trigger label + listbox selected state
       from its underlying <select>'s current value. ── */
  _syncCombobox(group) {
    const box = group.querySelector('[data-combobox]');
    if (!box) return;
    const native  = box.querySelector('select');
    const trigger = box.querySelector('[data-combobox-value]');
    const list    = box.querySelector('[data-combobox-list]');
    if (!native || !trigger) return;

    if (native.value) {
      trigger.textContent = native.value;
      trigger.classList.remove('is-placeholder');
    } else {
      trigger.textContent = trigger.closest('[data-combobox-trigger]').dataset.placeholder;
      trigger.classList.add('is-placeholder');
    }

    if (list) {
      list.querySelectorAll('[data-combobox-option]').forEach(li => {
        li.setAttribute('aria-selected', String(li.dataset.value === native.value));
      });
    }
  }

  /* ── Event wiring ── */
  _bindEvents() {
    this.el.addEventListener('change', e => {
      const target = e.target;

      /* Custom-text Yes/No toggle — not a variant option, handled separately */
      if (target.closest('[data-custom-text-group]')) {
        this._updateCustomText();
        this._sync(false);
        this._updateStepStatus();
        return;
      }

      const pos = parseInt(target.dataset.optionPos);
      const isRealOption = !isNaN(pos);
      if (isRealOption) this.selections[pos] = target.value;

      /* Update pill/swatch active state + label — real options (Material)
         and combo-only property fields (Color) both use this markup. */
      const pillGroup = target.closest('.sm-vp-group--pills');
      if (pillGroup) {
        pillGroup.querySelector('[data-cur-val]').textContent = target.value;
        pillGroup.querySelectorAll('.sm-vp-pill').forEach(p => {
          p.classList.toggle('is-active', p.querySelector('input').value === target.value);
        });
      }

      /* Keep the custom dropdown trigger/listbox in sync with whatever
         changed its underlying <select> (a listbox click, or the cascade
         filters below resetting it). */
      const dropdownGroup = target.closest('.sm-vp-group--dropdown');
      if (dropdownGroup) this._syncCombobox(dropdownGroup);

      /* Brand → Model cascade: real-variant-based for single-cover products
         (first two dropdowns globally drive it), property-based per
         data-cover for combo products (each cover's own Brand field only
         ever affects that same cover's Model list). */
      if (this.isCombo) {
        if (target.dataset.comboField === 'brand' && target.dataset.cover) {
          this._filterComboModels(target.dataset.cover);
        }
      } else if (isRealOption) {
        const dropdowns = Array.from(this.el.querySelectorAll('.sm-vp-group--dropdown select'));
        if (target === dropdowns[0]) this._filterModels();
      }

      this._sync(true);
      this._updateStepStatus();
    });

    /* Char counters — one per custom-text field */
    this.el.querySelectorAll('.sm-custom-text-field').forEach(field => {
      field.addEventListener('input', () => {
        const max = field.getAttribute('maxlength') || 30;
        const counter = this.el.querySelector(`.sm-ct-count[data-ct-count="${field.dataset.ctField}"]`);
        if (counter) counter.textContent = `${field.value.length} / ${max}`;
      });
    });

    /* Add to Cart guard — the button no longer looks disabled when the
       picker is incomplete (that read as "sold out"), so instead we catch
       the submit here, and if something's still unanswered we stop the
       submit and scroll the customer straight to it with an instruction,
       rather than silently doing nothing. */
    const form = document.getElementById(this.formId);
    if (form && !form.dataset.smAtcGuardBound) {
      form.dataset.smAtcGuardBound = 'true';
      form.addEventListener('submit', e => {
        const variant = this._findVariant();
        if (variant && !variant.available) return; // sold out — button is already disabled
        if (this._isComplete()) return; // all good, let it submit
        e.preventDefault();
        this._goToFirstIncompleteStep();
      });
    }
  }

  /* ── Scrolls to + announces whichever step still needs an answer ── */
  _goToFirstIncompleteStep() {
    let target = null;
    let message = 'Please complete all options before adding to cart.';

    if (this.isCombo) {
      for (const cover of ['1', '2']) {
        const acc = this.el.querySelector(`[data-cover-accordion="${cover}"]`);
        if (acc && !acc.classList.contains('is-complete')) {
          if (!acc.open) acc.open = true;
          target = acc;
          message = `Please finish Cover ${cover}'s options to continue.`;
          break;
        }
      }
    }

    if (!target) {
      const step = Array.from(this.el.querySelectorAll('.sm-vp-step')).find(s => !s.classList.contains('is-complete'));
      if (step) {
        target = step;
        const rawLabel = step.querySelector('.sm-vp-label, .sm-vp-cover-summary__title')?.textContent?.trim();
        if (rawLabel) {
          const label = rawLabel.split(':')[0].trim();
          message = `Please select ${label} to continue.`;
        }
      }
    }

    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('sm-vp-step--attention');
      setTimeout(() => target.classList.remove('sm-vp-step--attention'), 1600);
    }

    if (window.SMToast) window.SMToast.show(message);
  }

  /* ── Find the matching Shopify variant ── */
  _findVariant() {
    /* Product has no real variant options (has_only_default_variant) —
       our stepper renders nothing, so there's only ever one variant. */
    if (this.selections.length === 0) return this.variants[0] || null;

    return this.variants.find(v =>
      v.options.every((opt, i) => opt === this.selections[i])
    ) || null;
  }

  /* ── Has every step actually been answered? (gates Add to Cart) ──
       Combo products add two more requirement classes beyond the real
       Material options: every [data-custom-text-group] (one per cover, not
       just the first) and every combo-only property field (Brand/Model/
       Color per cover) must be answered too. */
  _isComplete() {
    const allOptionsChosen = this.selections.every(v => !!v);

    const ctGroups = Array.from(this.el.querySelectorAll('[data-custom-text-group]'));
    const ctAnswered = ctGroups.every(g => !!g.querySelector('input[type="radio"]:checked'));

    const comboFieldsAnswered = Array.from(this.el.querySelectorAll('.sm-vp-group[data-combo-field]')).every(g => {
      const field = g.dataset.comboField;
      if (field === 'brand' || field === 'model') return !!g.querySelector('select')?.value;
      if (field === 'color') return !!g.querySelector('input[type="radio"]:checked');
      return true;
    });

    return allOptionsChosen && ctAnswered && comboFieldsAnswered;
  }

  /* ── Sync form + UI to the matched variant ── */
  _sync(updatePrice) {
    const variant = this._findVariant();
    const form    = document.getElementById(this.formId);
    if (!form) return;

    /* Update hidden variant ID input */
    const idInput = form.querySelector('input[name="id"], .product-variant-id');
    if (idInput && variant) {
      idInput.value = variant.id;
      if (variant.available) {
        idInput.removeAttribute('disabled');
      } else {
        idInput.setAttribute('disabled', '');
      }
    }

    /* Update ATC button — only ever disabled for genuine sold-out. An
       incomplete picker no longer grays the button out (that read as
       "this product is unavailable" and confused customers); instead the
       submit handler below intercepts the click and guides them to
       whichever step still needs an answer. */
    const atcBtn = form.querySelector('[name="add"]');
    if (atcBtn) {
      if (!atcBtn.dataset.smLabel) atcBtn.dataset.smLabel = atcBtn.textContent.trim() || 'Add to Cart';
      if (variant && !variant.available) {
        atcBtn.disabled = true;
        atcBtn.textContent = 'Sold Out';
      } else {
        atcBtn.disabled = false;
        atcBtn.textContent = atcBtn.dataset.smLabel;
      }
    }

    /* Update price */
    if (updatePrice && variant) this._updatePrice(variant);

    /* Update variant summary row */
    this._updateSummary();
  }

  /* ── Direct DOM price update (no AJAX) ── */
  _updatePrice(variant) {
    const fmt = cents => {
      if (typeof SMUtils !== 'undefined') return SMUtils.formatMoney(cents);
      return '₹' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 0 });
    };

    const priceBlock = document.getElementById(`price-${this.secId}`);
    if (!priceBlock) return;

    const onSale = variant.compare_at_price > variant.price;
    priceBlock.classList.toggle('price--on-sale', onSale);

    /* Dawn's price markup has TWO elements sharing class .price-item--regular
       — one inside .price__regular (shown when not on sale) and one (the
       struck-through <s>) inside .price__sale (shown when on sale). They
       must be scoped by their wrapper, not picked by DOM order/:not() —
       that previously grabbed the wrong one, and .price-item--last and
       .price-item--sale were the SAME element, so the two writes below
       clobbered each other and the sale price ended up showing the
       compare-at price instead. */
    if (onSale) {
      const saleWrap  = priceBlock.querySelector('.price__sale');
      const saleEl     = saleWrap?.querySelector('.price-item--sale');
      const compareEl  = saleWrap?.querySelector('.price-item--regular');
      if (saleEl)    saleEl.textContent    = fmt(variant.price);
      if (compareEl) compareEl.textContent = fmt(variant.compare_at_price);
    } else {
      const regularWrap = priceBlock.querySelector('.price__regular');
      const regEl = regularWrap?.querySelector('.price-item--regular');
      if (regEl) regEl.textContent = fmt(variant.price);
    }

    /* Save % badge */
    const badge = document.querySelector('.sm-save-pct-badge');
    if (badge) {
      if (onSale) {
        const pct = Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100);
        badge.textContent = `Save ${pct}%`;
        badge.hidden = false;
      } else {
        badge.hidden = true;
      }
    }

    /* Sticky ATC price */
    const stickyPrice = document.querySelector('.sm-sticky-atc__price');
    if (stickyPrice) stickyPrice.textContent = fmt(variant.price);
  }

  /* ── Variant summary line ── */
  _updateSummary() {
    const el   = document.getElementById('sm-variant-summary');
    const text = document.getElementById('sm-variant-summary-text');
    if (!el || !text) return;
    const parts = this.selections.filter(Boolean);
    if (parts.length) {
      text.textContent = parts.join(' · ');
      el.hidden = false;
    }
  }

  /* ── Brand → Model cascade — narrows the Model dropdown to that brand's
       models, but never auto-picks one; the customer must choose it themselves.
       Hiding is done on the custom listbox <li> elements via the real `hidden`
       attribute — hiding native <option> elements via CSS is unreliable
       cross-browser (Safari in particular ignores display:none on <option>,
       which is why every model used to show regardless of brand). ── */
  _filterModels() {
    const groups = Array.from(this.el.querySelectorAll('.sm-vp-group--dropdown'));
    if (groups.length < 2) return;

    const [brandGroup, modelGroup] = groups;
    const brandSel = brandGroup.querySelector('select');
    const modelSel = modelGroup.querySelector('select');
    if (!brandSel || !modelSel) return;
    const brand = brandSel.value;

    const valid = new Set(
      this.variants
        .filter(v => v.options[0] === brand)
        .map(v => v.options[1])
    );

    Array.from(modelSel.options).forEach(opt => {
      if (!opt.value) return; // keep the placeholder option untouched
      opt.disabled = !valid.has(opt.value);
    });

    const modelList = modelGroup.querySelector('[data-combobox-list]');
    if (modelList) {
      modelList.querySelectorAll('[data-combobox-option]').forEach(li => {
        const ok = valid.has(li.dataset.value);
        li.dataset.brandHidden = ok ? 'false' : 'true';
        li.toggleAttribute('aria-disabled', !ok);
        this._applyOptionVisibility(li);
      });
    }

    /* Whatever was previously chosen no longer applies to the new brand —
       reset back to the placeholder rather than guessing a replacement. */
    if (!valid.has(modelSel.value)) {
      modelSel.value = '';
      this.selections[1] = null;
      this._syncCombobox(modelGroup);
    }
  }

  /* ── Yes/No custom text field reveal — one per cover in combo mode, so
       this updates every [data-custom-text-group] on the page, not just
       the first (each toggle's own change only actually flips its own
       group; re-running the others is a harmless no-op). ── */
  _updateCustomText() {
    this.el.querySelectorAll('[data-custom-text-group]').forEach(group => {
      const isYes = !!group.querySelector('[data-triggers-text="true"]:checked');
      const wrap  = group.querySelector('.sm-custom-text-wrap');
      if (!wrap) return;

      wrap.hidden = !isYes;
      if (!isYes) {
        wrap.querySelectorAll('.sm-custom-text-field').forEach(f => { f.value = ''; });
        wrap.querySelectorAll('.sm-ct-count').forEach(c => {
          const field = wrap.querySelector(`.sm-custom-text-field[data-ct-field="${c.dataset.ctCount}"]`);
          const max = field ? (field.getAttribute('maxlength') || 30) : 30;
          c.textContent = `0 / ${max}`;
        });
      }
    });
  }

  /* ── Combo products' per-cover Brand → Model cascade. Unlike
       _filterModels (variant-based, single-cover products), Model isn't a
       real Shopify option here — validity comes from each <li>'s own
       data-brand attribute, and each cover's Brand only ever filters that
       same cover's Model list (scoped by data-cover). ── */
  _filterComboModels(cover) {
    const brandSelect = this.el.querySelector(`select[data-combo-field="brand"][data-cover="${cover}"]`);
    const modelGroup  = this.el.querySelector(`.sm-vp-group[data-combo-field="model"][data-cover="${cover}"]`);
    if (!brandSelect || !modelGroup) return;

    const brand = brandSelect.value;
    const modelSelect = modelGroup.querySelector('select');
    const modelList = modelGroup.querySelector('[data-combobox-list]');

    if (modelList) {
      modelList.querySelectorAll('[data-combobox-option]').forEach(li => {
        const ok = li.dataset.brand === brand;
        li.dataset.brandHidden = ok ? 'false' : 'true';
        li.toggleAttribute('aria-disabled', !ok);
        this._applyOptionVisibility(li);
      });
    }

    /* Whatever was previously chosen no longer applies to the new brand —
       reset back to the placeholder rather than guessing a replacement. */
    if (modelSelect.value) {
      const stillValid = modelList?.querySelector(
        `[data-combobox-option][data-value="${CSS.escape(modelSelect.value)}"][data-brand="${CSS.escape(brand)}"]`
      );
      if (!stillValid) {
        modelSelect.value = '';
        this._syncCombobox(modelGroup);
      }
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.sm-vp').forEach(el => new SMVariantPicker(el));
});
